import { loadStripe } from "@stripe/stripe-js";
import { Config } from "./Config.js";
import { ApiService } from "./ApiService.js";

/**
 * Thrown when the API refuses the checkout because a shuttle does not have
 * enough seats left (HTTP 422). Carries the per-shuttle shortage detail so the
 * widget can tell the customer exactly how many seats remain.
 */
export class CapacityError extends Error {
    constructor(shortages = []) {
        super("insufficient_capacity");
        this.name = "CapacityError";
        this.shortages = shortages;
    }
}

export class PaymentController {

    static async createCheckout(cartData, container, completeListener) {
        this.checkout?.destroy();
        const stripParams = Config.get("stripReturnParams");
        const returnUrl = (stripParams == true)
            ? `${window.location.origin}${window.location.pathname}`
            : window.location.href;

        cartData.cart.returnUrl = returnUrl;

        const response = await ApiService.createSession(cartData);

        if (!response.ok) {
            // 422 means a shuttle sold out from under the cart. Pull the
            // per-shuttle remaining counts out of the body so the customer can
            // be told how many seats are actually left, rather than a generic
            // failure.
            if (response.status === 422) {
                let shortages = [];
                try {
                    const body = await response.json();
                    shortages = Array.isArray(body?.shortages) ? body.shortages : [];
                } catch {
                    // No/invalid body — fall through with an empty shortage list.
                }
                throw new CapacityError(shortages);
            }
            throw new Error(response.statusText);
        }

        const { sessionId, stripeAccount } = await response.json();

        // The checkout is a direct charge on the provider's connected account
        // (provider = merchant of record), so Stripe.js must be scoped to that
        // account. Cache one Stripe instance per account; the publishable key is
        // the platform's and only needs fetching once.
        if (this.publicKey == null) {
            const keyResponse = await ApiService.getStripeKey();
            const { publicKey } = await keyResponse.json();
            this.publicKey = publicKey;
        }

        this.stripeByAccount = this.stripeByAccount || {};
        const accountKey = stripeAccount || "platform";
        if (this.stripeByAccount[accountKey] == null) {
            const options = stripeAccount ? { stripeAccount } : undefined;
            this.stripeByAccount[accountKey] = await loadStripe(this.publicKey, options);
        }
        const stripe = this.stripeByAccount[accountKey];

        this.checkout = await stripe.initEmbeddedCheckout({ clientSecret: sessionId, onComplete: completeListener });
        this.checkout.mount(container);
    }
}
